import { normalizeTargetTypes } from "./templateScopeUtils";
import {
  parseThumbnailFromTemplate,
  serializeGeneratedThumbnailUrl,
  serializeNoThumbnailUrl,
} from "./templateThumbnailCatalog";
import { getDefaultValueForType } from "../../../shared/custom-data/field-types";
import { resolveTypeKey } from "../../../shared/custom-data/field-types";
import { sanitizeCurrencyForApi } from "../../../shared/custom-data/field-types/parsers";

/** Shown when the API returns a template with no title (e.g. pre-migration rows). */
export const UNTITLED_TEMPLATE_LABEL = "Untitled template";

/**
 * Label for a normalized template; uses persisted name on the record when catalog title is empty.
 */
export const getTemplateDisplayName = (template, persistedName = "") => {
  const saved = String(persistedName ?? "").trim();
  if (!template) return saved;
  const title = String(template.title ?? "").trim();
  if (title) return title;
  return saved || template.name || UNTITLED_TEMPLATE_LABEL;
};

const getTemplateFieldDefault = (type) => getDefaultValueForType(type);

const normalizeTemplateField = (field) => {
  if (!field || typeof field !== "object") return null;
  const name = String(field.name || "").trim();
  if (!name) return null;

  const type = resolveTypeKey(field.type || "STRING");
  const normalized = {
    name,
    type,
    value: field.value ?? getTemplateFieldDefault(type),
  };

  if (type === "URL") {
    normalized.value = typeof normalized.value === "string" ? normalized.value.trim() : "";
  }

  if (type === "CURRENCY") {
    const raw = normalized.value;
    normalized.value =
      raw && typeof raw === "object" ? sanitizeCurrencyForApi(raw) : null;
  }

  if (type === "COMBOLIST" || type === "ENUM") {
    normalized.options = Array.isArray(field.options) ? field.options : [];
  }

  if (type === "PERIOD") {
    normalized.startDate = field.startDate ?? null;
    normalized.endDate = field.endDate ?? null;
  }

  return normalized;
};

export const cloneTemplateFieldsForDraft = (fields) => {
  if (!Array.isArray(fields)) return [];
  return fields
    .map(normalizeTemplateField)
    .filter(Boolean)
    .map((field) => ({
      ...field,
      ...(Array.isArray(field.options) ? { options: [...field.options] } : {}),
      ...(field.type === "PERIOD"
        ? {
            startDate: field.startDate ?? null,
            endDate: field.endDate ?? null,
          }
        : {}),
    }));
};

const normalizeCreateField = (field) => {
  if (!field || typeof field !== "object") return null;
  const name = String(field.name || "").trim();
  if (!name) return null;

  const type = resolveTypeKey(field.type || "STRING");
  const base = { name, type };

  if (type === "COMBOLIST" || type === "ENUM") {
    return {
      ...base,
      options: Array.isArray(field.options) ? field.options : [],
    };
  }

  return base;
};

export const normalizeTemplate = (template, options = {}) => {
  if (!template || typeof template !== "object") return null;
  const id = template.id || template.key || template.templateId || null;
  const source = options.source || template.source || "root";
  if (!id) return null;

  const title = String(template.title ?? "").trim();
  const name = title || UNTITLED_TEMPLATE_LABEL;

  const fields = Array.isArray(template.fields)
    ? template.fields.map(normalizeTemplateField).filter(Boolean)
    : [];

  const thumbnailURL = String(
    template.thumbnailURL || template.thumbnailUrl || ""
  ).trim();

  return {
    id,
    title,
    name,
    desc: String(template.desc || template.description || "").trim(),
    fields,
    source: source === "user" ? "user" : "root",
    ownerUserId: template.userId || null,
    targetTypes: normalizeTargetTypes(template.targetTypes),
    thumbnailURL,
    thumbnail: parseThumbnailFromTemplate(template),
  };
};

export const mergeTemplateIntoCustomData = (customData, template) => {
  if (!template) return Array.isArray(customData) ? customData : [];

  const existing = Array.isArray(customData) ? [...customData] : [];
  const byName = new Map(
    existing.map((field) => [String(field?.name || "").trim(), field]).filter(([name]) => !!name)
  );

  template.fields.forEach((field) => {
    const key = String(field.name || "").trim();
    if (!key) return;
    if (byName.has(key)) return;
    const fieldType = String(field.type || "STRING").toUpperCase();
    byName.set(key, {
      name: key,
      type: fieldType,
      value: field.value ?? getTemplateFieldDefault(fieldType),
      ...(fieldType === "COMBOLIST" || fieldType === "ENUM"
        ? { options: Array.isArray(field.options) ? field.options : [] }
        : {}),
      ...(fieldType === "PERIOD"
        ? {
            startDate: field.startDate ?? null,
            endDate: field.endDate ?? null,
          }
        : {}),
    });
  });

  return Array.from(byName.values());
};

export const replaceCustomDataWithTemplate = (template) => {
  if (!template) return [];
  return cloneTemplateFieldsForDraft(template.fields);
};

export const applyTemplateToEntityDraft = (entity, template) => {
  if (!entity || !template) return entity;
  return {
    ...entity,
    customData: mergeTemplateIntoCustomData(entity.customData, template),
    customDataTemplateId: template.id,
    customDataTemplateName: template.name,
  };
};

/**
 * Remove template binding and all custom data values from an entity draft.
 */
export const clearEntityTemplateFields = (entity) => {
  if (!entity || typeof entity !== "object") return entity;
  return {
    ...entity,
    customData: [],
    customDataTemplateId: "",
    customDataTemplateName: "",
  };
};

export const applyTemplateToEntityDraftReplacingFields = (entity, template) => {
  if (!entity || !template) return entity;
  return {
    ...entity,
    customData: replaceCustomDataWithTemplate(template),
    customDataTemplateId: template.id,
    customDataTemplateName: template.name,
  };
};

export const buildTemplateCreatePayload = (
  fields,
  { name, desc, targetTypes, thumbnail, source } = {}
) => {
  const normalizedFields = Array.isArray(fields)
    ? fields.map(normalizeCreateField).filter(Boolean)
    : [];

  const payload = {
    title: String(name || "").trim(),
    desc: String(desc || "").trim(),
    fields: normalizedFields,
  };

  const normalizedTargets = normalizeTargetTypes(targetTypes);
  if (normalizedTargets.length > 0) {
    payload.targetTypes = normalizedTargets;
  }

  const normalizedSource = source === "root" ? "root" : "user";
  if (normalizedSource === "user") {
    if (thumbnail?.mode === "none") {
      payload.thumbnailURL = serializeNoThumbnailUrl();
    } else if (thumbnail?.mode === "image") {
      const imageUrl = String(thumbnail?.imageUrl || "").trim();
      payload.thumbnailURL = imageUrl || serializeNoThumbnailUrl();
    } else {
      payload.thumbnailURL = serializeGeneratedThumbnailUrl({
        iconKey: thumbnail?.iconKey,
        backgroundColor: thumbnail?.backgroundColor,
      });
    }
  }

  return payload;
};
